/**
 * <luxury-sticky-atc>
 * Mirrors the native product form and delegates add-to-cart back to it.
 */
(function () {
  if (customElements.get('luxury-sticky-atc')) return;

  class LuxuryStickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productInfo = document.getElementById(`MainProduct-${this.sectionId}`) || this.closest('product-info');
      this.nativeForm = document.getElementById(`product-form-${this.sectionId}`);
      this.nativeButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      this.nativeQuantity = this.productInfo?.querySelector('.product-form__quantity .quantity__input');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.variantEl = this.querySelector('[data-sticky-variant]');
      this.imageEl = this.querySelector('[data-sticky-image]');
      this.qtyInput = this.querySelector('[data-sticky-qty]');
      this.addButton = this.querySelector('[data-sticky-submit]');
      this.minusButton = this.querySelector('[data-sticky-minus]');
      this.plusButton = this.querySelector('[data-sticky-plus]');
      this._scrollFrame = null;

      this._onScroll = () => this.queueVisibilityCheck();
      this._onNativeQuantity = () => this.syncFromNativeQuantity();
      this._onStickyQuantity = () => this.syncToNativeQuantity();
      this._onSubmit = () => this.submitNativeForm();
      this._onMinus = () => this.stepQuantity(-1);
      this._onPlus = () => this.stepQuantity(1);
      this._onCartSettled = () => this.setLoading(false);

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });
      document.addEventListener('luxury-cart:updated', this._onCartSettled);
      this.nativeQuantity?.addEventListener('change', this._onNativeQuantity);
      this.qtyInput?.addEventListener('change', this._onStickyQuantity);
      this.addButton?.addEventListener('click', this._onSubmit);
      this.minusButton?.addEventListener('click', this._onMinus);
      this.plusButton?.addEventListener('click', this._onPlus);

      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
        this._variantUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data?.sectionId !== this.sectionId) return;
          this.updateVariant(data.variant);
        });
      }

      this.syncFromNativeQuantity();
      this.syncButtonState();
      this.queueVisibilityCheck();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
      document.removeEventListener('luxury-cart:updated', this._onCartSettled);
      this.nativeQuantity?.removeEventListener('change', this._onNativeQuantity);
      this.qtyInput?.removeEventListener('change', this._onStickyQuantity);
      this.addButton?.removeEventListener('click', this._onSubmit);
      this.minusButton?.removeEventListener('click', this._onMinus);
      this.plusButton?.removeEventListener('click', this._onPlus);
      this._variantUnsubscriber?.();
    }

    queueVisibilityCheck() {
      if (this._scrollFrame) return;
      this._scrollFrame = requestAnimationFrame(() => {
        this._scrollFrame = null;
        this.updateVisibility();
      });
    }

    updateVisibility() {
      if (!this.nativeButton || !this.productInfo) return;
      const productRect = this.productInfo.getBoundingClientRect();
      const buttonRect = this.nativeButton.getBoundingClientRect();
      const nativeButtonVisible = buttonRect.bottom > 0 && buttonRect.top < window.innerHeight;
      const productStillInView = productRect.bottom > window.innerHeight * 0.28;
      const shouldShow = window.scrollY > 260 && productStillInView && !nativeButtonVisible;

      this.classList.toggle('is-visible', shouldShow);
      document.body.classList.toggle('lux-sticky-atc-visible', shouldShow);
    }

    syncFromNativeQuantity() {
      if (!this.nativeQuantity || !this.qtyInput) return;
      this.qtyInput.value = this.nativeQuantity.value || 1;
      this.qtyInput.min = this.nativeQuantity.min || 1;
      this.qtyInput.max = this.nativeQuantity.max || '';
      this.qtyInput.step = this.nativeQuantity.step || 1;
      this.syncButtonState();
    }

    syncToNativeQuantity() {
      if (!this.nativeQuantity || !this.qtyInput) return;
      const value = this.normalizeQuantity(Number(this.qtyInput.value || 1));
      this.qtyInput.value = value;
      this.nativeQuantity.value = value;
      this.nativeQuantity.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncButtonState();
    }

    stepQuantity(direction) {
      if (!this.qtyInput) return;
      const step = Number(this.qtyInput.step || 1);
      const current = Number(this.qtyInput.value || 1);
      this.qtyInput.value = this.normalizeQuantity(current + direction * step);
      this.syncToNativeQuantity();
    }

    normalizeQuantity(value) {
      const min = Number(this.qtyInput?.min || 1);
      const max = Number(this.qtyInput?.max || Infinity);
      return Math.max(min, Math.min(max, Number.isFinite(value) ? value : min));
    }

    submitNativeForm() {
      if (!this.nativeButton || this.nativeButton.disabled || this.nativeButton.getAttribute('aria-disabled') === 'true') return;
      this.setLoading(true);
      this.nativeButton.click();
      setTimeout(() => this.setLoading(false), 7000);
    }

    updateVariant(variant) {
      if (!variant) {
        this.setUnavailable(true);
        return;
      }

      this.setUnavailable(!variant.available);

      if (this.priceEl) this.priceEl.textContent = window.LuxuryCartUtils?.formatMoney(variant.price) || this.priceEl.textContent;
      if (this.variantEl) this.variantEl.textContent = this.getVariantLabel(variant);
      if (this.imageEl && variant.featured_media?.preview_image?.src) {
        this.imageEl.src = variant.featured_media.preview_image.src;
        this.imageEl.alt = variant.featured_media.alt || this.dataset.productTitle || '';
      }

      this.syncFromNativeQuantity();
    }

    getVariantLabel(variant) {
      if (!variant.title || variant.title === 'Default Title') return this.dataset.defaultVariantLabel || 'Selected size';
      return variant.title;
    }

    syncButtonState() {
      if (!this.addButton || !this.nativeButton) return;
      const disabled = this.nativeButton.disabled || this.nativeButton.getAttribute('aria-disabled') === 'true';
      this.addButton.toggleAttribute('disabled', disabled);
    }

    setUnavailable(isUnavailable) {
      this.classList.toggle('is-unavailable', isUnavailable);
      this.addButton?.toggleAttribute('disabled', isUnavailable);
    }

    setLoading(isLoading) {
      this.addButton?.classList.toggle('is-loading', isLoading);
      this.addButton?.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      if (!isLoading) this.syncButtonState();
    }
  }

  customElements.define('luxury-sticky-atc', LuxuryStickyAtc);
})();
